import React from 'react'
import { CgImage } from 'react-icons/cg'
import { FiGlobe, FiExternalLink, FiVideo } from 'react-icons/fi'

import { useCustomization } from 'common/contexts/Customization/useCustomization'
import { getLandOrPortExThumb } from 'common/utils/exerciseUtils'
import { useListenExercisesQuery } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { useListenCoachVideosQuery } from 'modules/VideoLibrary/videoLibraryApi'
import { useListenOrgProgramsListQuery, useListenWorkoutQuery } from 'modules/Programs/programApi'
import { useListenCollectionsQuery } from 'modules/Collections/collectionsApi'
import { useListenProgramGroupsQuery } from 'modules/Layout/layoutApi'

import { availableScenes } from '../CommunityPostForm/SceneInput'
import { getGroupCallState, getLinkDomain, GROUP_CALL_TYPE } from '../postButtonUtils'
import { formatGroupCallWindow } from '../timeZoneUtils'
import FittedThumb from './FittedThumb'

const TYPE_LABELS = {
  video: 'Video',
  program: 'Program',
  workout: 'Workout',
  exercise: 'Exercise',
  scene: 'App page',
  collection: 'Collection',
  programGroup: 'Series',
}

// Preview of what a post's button opens (video/program/workout/exercise/collection/
// series/app page/link):
// thumbnail, the content's actual title, and a type label. Mirrors the mobile
// attachment card, minus the chevron — on the dashboard it isn't tappable.
// Returns null for posts without a button.
export function usePostAttachment(post, coachOrgId) {
  const linkType = post?.linkType
  const isContent = ['video', 'program', 'workout', 'exercise', 'collection', 'programGroup', 'scene'].includes(
    linkType
  )
  const { exVidOrientation } = useCustomization()

  const { data: videosData } = useListenCoachVideosQuery({ coachOrgId }, { skip: linkType !== 'video' })
  const { data: programsData } = useListenOrgProgramsListQuery({ orgId: coachOrgId }, { skip: linkType !== 'program' })
  const { data: workoutData } = useListenWorkoutQuery(
    { orgId: coachOrgId, workoutId: post?.workoutId },
    { skip: linkType !== 'workout' || !post?.workoutId }
  )
  const { data: exercisesData } = useListenExercisesQuery(
    { coachOrgId, exVidOrientation },
    { skip: linkType !== 'exercise' }
  )
  const { data: collectionsData } = useListenCollectionsQuery({ coachOrgId }, { skip: linkType !== 'collection' })
  const { data: programGroupsData } = useListenProgramGroupsQuery({ coachOrgId }, { skip: linkType !== 'programGroup' })

  // A group call shows when it happens rather than where it points — the coach
  // already knows it's their Zoom room, what they need to check is the schedule.
  if (linkType === GROUP_CALL_TYPE) {
    return {
      title: formatGroupCallWindow(post) || getLinkDomain(post.link) || 'Group call',
      typeLabel: 'Group call',
      thumbnail: post?.imageUrl || null,
      isGroupCall: true,
      isLive: getGroupCallState(post) === 'live',
      isEnded: getGroupCallState(post) === 'ended',
      isExternal: true,
    }
  }

  if (linkType === 'link') {
    return {
      title: getLinkDomain(post.link) || 'Link',
      typeLabel: 'Link',
      // A link has no artwork of its own, so the card shows the post photo when
      // there is one and a globe icon when there isn't.
      thumbnail: post?.imageUrl || null,
      isLink: true,
      isExternal: post?.openInDefaultBrowser === true,
    }
  }

  if (!isContent) return null

  const videoLoading = linkType === 'video' && (!videosData || videosData.isLoading === true)
  const programLoading = linkType === 'program' && (!programsData || programsData.stillLoading === true)
  const workoutLoading =
    linkType === 'workout' && Boolean(post?.workoutId) && (!workoutData || workoutData.isLoading === true)
  // The exercise library hook starts out as a filler record until Firebase answers.
  const exerciseLoading = linkType === 'exercise' && (!exercisesData || exercisesData.isFiller === true)
  const collectionLoading = linkType === 'collection' && (!collectionsData || collectionsData.isLoading === true)
  const programGroupLoading =
    linkType === 'programGroup' && (!programGroupsData || programGroupsData.isLoading === true)
  const isLoading =
    videoLoading || programLoading || workoutLoading || exerciseLoading || collectionLoading || programGroupLoading

  const video = linkType === 'video' && !videosData?.isLoading ? videosData?.[post.videoId] : null
  const program = linkType === 'program' && !programsData?.stillLoading ? programsData?.[post.programId] : null
  const workout = linkType === 'workout' && !workoutData?.isLoading ? workoutData : null
  const exercise = linkType === 'exercise' && !exerciseLoading ? exercisesData?.exercises?.[post.exerciseId] : null
  const collection = linkType === 'collection' && !collectionLoading ? collectionsData?.[post.collectionId] : null
  const programGroup =
    linkType === 'programGroup' && !programGroupLoading ? programGroupsData?.[post.programGroupId] : null
  const scene = linkType === 'scene' ? availableScenes.find((s) => s.value === post.scene) : null

  const contentImg =
    video?.previewImg ||
    program?.previewImg ||
    workout?.previewImg ||
    (exercise && getLandOrPortExThumb(exVidOrientation, exercise)) ||
    collection?.previewImg ||
    programGroup?.previewImg ||
    null
  return {
    isLoading,
    title:
      video?.title ||
      program?.name ||
      workout?.title ||
      exercise?.name ||
      collection?.name ||
      programGroup?.name ||
      scene?.displayName ||
      TYPE_LABELS[linkType],
    typeLabel: TYPE_LABELS[linkType],
    // The post photo is the button's thumbnail, so it comes first: a coach upload
    // replaces the linked content's artwork (and an app page's standard artwork).
    // Posts with no photo fall back to the live content artwork.
    thumbnail: post?.imageUrl || contentImg || scene?.defaultImg || null,
  }
}

export default function PostAttachmentCard({ attachment }) {
  if (!attachment) return null

  if (attachment.isLoading) {
    return (
      <div className='self-start flex items-center mt-3 p-2 pr-3 rounded-lg border border-gray-200 bg-gray-50 w-96 max-w-full'>
        <div className='w-[84px] aspect-video rounded-md bg-gray-200 shrink-0 mr-3 animate-pulse' />
        <div className='min-w-0 flex-1'>
          <div className='h-3.5 w-32 max-w-full bg-gray-200 animate-pulse' />
          <div className='h-3 w-16 mt-1.5 bg-gray-200 animate-pulse' />
        </div>
      </div>
    )
  }

  return (
    <div className='self-start flex items-center mt-3 p-2 pr-3 rounded-lg border border-gray-200 bg-gray-50 w-96 max-w-full'>
      {/* The tile keeps the same 16:9 box whether it holds a photo or a placeholder
          icon, so cards line up whatever the button opens. */}
      <div className='relative w-[84px] aspect-video rounded-md overflow-hidden bg-gray-200 shrink-0 mr-3 flex items-center justify-center'>
        {attachment.thumbnail ? (
          <FittedThumb src={attachment.thumbnail} alt={attachment.title} />
        ) : attachment.isGroupCall ? (
          <FiVideo className='text-gray-500 w-5 h-5' />
        ) : attachment.isLink ? (
          <FiGlobe className='text-gray-500 w-5 h-5' />
        ) : (
          <CgImage className='text-gray-400 w-5 h-5' />
        )}
      </div>
      <div className='min-w-0 flex-1'>
        {/* A group call title is a date range, not a name — it gets a lighter weight
            and is allowed to wrap onto a second line instead of being cut off. */}
        <div
          className={
            attachment.isGroupCall
              ? 'text-sm font-medium text-tBlack break-words'
              : 'text-sm font-semibold text-tBlack truncate'
          }
        >
          {attachment.title}
        </div>
        <div className='flex items-center text-xs text-gray-500'>
          {attachment.typeLabel}
          {attachment.isLive && (
            <span className='ml-2 rounded bg-tRed px-1.5 py-0.5 text-[10px] font-extrabold text-white'>LIVE</span>
          )}
          {attachment.isEnded && (
            <span className='ml-2 rounded bg-gray-400 px-1.5 py-0.5 text-[10px] font-extrabold text-white'>ENDED</span>
          )}
        </div>
      </div>
      {attachment.isGroupCall ? (
        <FiVideo className='text-gray-500 w-[18px] h-[18px] shrink-0 ml-2' />
      ) : (
        attachment.isExternal && <FiExternalLink className='text-gray-500 w-4 h-4 shrink-0 ml-2' />
      )}
    </div>
  )
}
